import * as THREE from 'three';
import {
  t,
  type SectorCoord,
  sectorKey,
  sectorName,
  warpTravelCostFor,
  sectorHasWarpGate,
  getZoneDifficulty,
  ZONE_TIER_KEYS,
  summarizeSectorNPCs,
  type SectorNPCSummary,
  RACE_INFO,
  dominantRace,
  type SectorClaim,
  type ActiveSectorEvent,
  FREE_WARP_MAX_JUMP,
  isWarpJumpAllowed,
} from '@viral-space/shared';
import { attachMobileCloseButton, isMobileLayout } from './ui-shared';

const COLORS = {
  primary: '#00ccff',
  primaryNum: 0x00ccff,
  secondary: '#ffb000',
  danger: '#ff4444',
  dim: 'rgba(0,204,255,0.35)',
  dimNum: 0x0077aa,
  current: '#00ff41',
  currentNum: 0x00ff41,
  bg: 'rgba(0,8,20,0.92)',
  panelBg: 'rgba(0,8,24,0.85)',
  // Sectors the player knows about but which have no warp gate of their
  // own — they remain visible (and selectable so the UI can explain why
  // they can't be warped to) but are rendered as dim grey "dead" nodes
  // to visually distinguish them from real gate destinations.
  noGateNum: 0x55667a,
  noGate: '#55667a',
  // Severity-coloured ring for sectors with an active world event.
  // Bright orange / red so it never collides with the warp-gate cyan
  // palette and gives a glance-readable "something is happening here".
  eventInfo: '#ffb000',
  eventInfoNum: 0xffb000,
  eventWarning: '#ff8a1a',
  eventWarningNum: 0xff8a1a,
  eventDanger: '#ff5050',
  eventDangerNum: 0xff5050,
  eventSuccess: '#3affb0',
  eventSuccessNum: 0x3affb0,
  // Galactic center marker — gold so it's instantly distinguishable
  // from the cyan warp-gate cloud, the green "you are here" tint, and
  // the orange/red event ring palette.
  origin: '#ffd700',
  originNum: 0xffd700,
} as const;

const FONT = "'Share Tech Mono', monospace";
const GLOW = `0 0 8px ${COLORS.primary}, 0 0 16px rgba(0,204,255,0.3)`;
const CURRENT_GLOW = `0 0 8px ${COLORS.current}, 0 0 16px rgba(0,255,65,0.3)`;

/**
 * Per-tier label colour for the warp-gate info panel. The cyan-heavy
 * warp-gate palette doesn't carry a "danger gradient" of its own, so we
 * borrow one keyed off the universe-map's zone tints — players see the
 * same colour-language in both maps.
 */
const WARPGATE_TIER_COLORS: Record<string, string> = {
  contested: '#ffb000',
  embattled: '#ff8a1a',
  void: '#aa66ff',
  abyss: '#cc44ff',
  nightmare: '#ff2244',
  apex: '#ff44aa',
  singularity: '#22ddff',
  transcendent: '#aaeeff',
  beyond: '#fff7c0',
};

export interface WarpGateUIOptions {
  onTravel: (destination: SectorCoord) => void;
  onClose: () => void;
  /**
   * Open the Premium upgrade modal. Fired by the "unlock direct jump"
   * CTA shown to free pilots when they select a destination beyond
   * FREE_WARP_MAX_JUMP. Optional so tests / legacy hosts keep working.
   */
  onUpgrade?: () => void;
}

/**
 * 3D hologram-style sector map shown when the player activates a warp gate.
 * Uses its own Three.js scene so it doesn't interfere with the main world.
 */
export class WarpGateUI {
  private container: HTMLElement;
  private root!: HTMLElement;
  private canvas!: HTMLCanvasElement;
  private infoPanel!: HTMLElement;
  private selectedInfoEl!: HTMLElement;
  private selectedOwnerEl!: HTMLElement;
  private selectedZoneEl!: HTMLElement;
  private selectedZoneDescEl!: HTMLElement;
  private selectedNpcSummaryEl!: HTMLElement;
  private costEl!: HTMLElement;
  private travelBtn!: HTMLButtonElement;
  private cancelBtn!: HTMLButtonElement;
  private errorEl!: HTMLElement;

  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private hologramRoot: THREE.Group;
  private sectorMeshes: Map<string, THREE.Mesh> = new Map();
  private linkLines: THREE.Line[] = [];
  private raycaster = new THREE.Raycaster();
  private pointer = new THREE.Vector2();

  private current: SectorCoord = { x: 0, y: 0, z: 0 };
  private discovered: SectorCoord[] = [];
  private selected: SectorCoord | null = null;
  private credits = 0;
  /**
   * Premium status echo from the parent Game. Used purely for client-
   * side cost display so the panel surfaces the same discounted price
   * the server will actually charge — server still re-computes the
   * authoritative number on `WarpGateUse`. Lives as a plain field so
   * a webhook-pushed `PremiumUpdate` can flip it via `setPremium()`
   * without re-instantiating the panel.
   */
  private hasPremium = false;
  private claimByKey = new Map<string, SectorClaim>();
  // Active world events keyed by sector key — the warp-gate hologram
  // paints an indicator on each affected sector and shows the event
  // description in the right-hand info panel when one is selected.
  private eventsByKey = new Map<string, ActiveSectorEvent[]>();
  // Lazy-built event card slot; injected into the info panel during
  // build() and shown only when the selected sector has an event.
  private eventInfoEl!: HTMLElement;

  private visible = false;
  private animFrame = 0;
  private time = 0;

  private pointerDown: { x: number; y: number; time: number } | null = null;
  private dragging = false;
  private rotY = 0.35;
  private rotX = -0.45;
  private zoom = 1;

  /**
   * Display mode toggle. The 3D hologram is the default fancy view, but
   * once a pilot has discovered a lot of warp-enabled sectors it gets
   * crowded — `'list'` switches the body into a sortable, scrollable
   * sector list so the player can pick a destination by name without
   * fighting the camera. The right-hand info/cost/travel panel stays
   * the same; only what's left of it changes.
   */
  private viewMode: 'map' | 'list' = 'map';
  private viewToggleBar!: HTMLElement;
  private viewMapBtn!: HTMLButtonElement;
  private viewListBtn!: HTMLButtonElement;
  private listContainer!: HTMLElement;

  private onTravelCb: (d: SectorCoord) => void;
  private onCloseCb: () => void;
  private onUpgradeCb: (() => void) | null;
  private upgradeBtn!: HTMLButtonElement;

  constructor(container: HTMLElement, opts: WarpGateUIOptions) {
    this.container = container;
    this.onTravelCb = opts.onTravel;
    this.onCloseCb = opts.onClose;
    this.onUpgradeCb = opts.onUpgrade ?? null;
    this.scene = new THREE.Scene();
    this.hologramRoot = new THREE.Group();
    this.scene.add(this.hologramRoot);
    this.build();
  }

  /**
   * Whether the selected destination is beyond the free-account jump
   * range. Mirrors the server-side FREE_WARP_MAX_JUMP gate (same
   * shared predicate) so the UI can show the Premium lock instead of
   * letting the request fail.
   */
  private isRangeLocked(dest: SectorCoord): boolean {
    return !isWarpJumpAllowed(this.current, dest, this.hasPremium);
  }

  /**
   * Mirror the latest premium snapshot from the parent Game so cost
   * previews match what the server will charge. Re-renders the
   * currently-selected info panel + visible list rows immediately
   * because the cost number is the only thing that changes.
   */
  setPremium(hasPremium: boolean): void {
    if (this.hasPremium === hasPremium) return;
    this.hasPremium = hasPremium;
    if (this.visible) {
      this.select(this.selected);
    }
  }

  private el(tag: string, styles: Partial<CSSStyleDeclaration>, text?: string): HTMLElement {
    const e = document.createElement(tag);
    Object.assign(e.style, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build(): void {
    this.root = this.el('div', {
      position: 'absolute',
      inset: '0',
      display: 'none',
      flexDirection: 'column',
      fontFamily: FONT,
      color: COLORS.primary,
      background: COLORS.bg,
      zIndex: '80',
      pointerEvents: 'auto',
      cursor: 'default',
      overflow: 'hidden',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    });
    this.container.appendChild(this.root);

    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 24px',
      borderBottom: `1px solid ${COLORS.dim}`,
      flexShrink: '0',
    });

    const titleBlock = this.el('div', { display: 'flex', flexDirection: 'column', gap: '2px' });
    titleBlock.appendChild(
      this.el('div', {
        fontSize: 'clamp(14px, 2vw, 22px)',
        letterSpacing: '4px',
        textShadow: GLOW,
      }, t('warpGate.title')),
    );
    titleBlock.appendChild(
      this.el('div', {
        fontSize: '10px',
        color: 'rgba(0,204,255,0.6)',
      }, t('warpGate.subtitle')),
    );
    header.appendChild(titleBlock);

    this.cancelBtn = document.createElement('button');
    Object.assign(this.cancelBtn.style, {
      fontFamily: FONT,
      fontSize: 'clamp(11px, 1.3vw, 14px)',
      padding: '6px 18px',
      background: 'transparent',
      border: `2px solid ${COLORS.danger}`,
      color: COLORS.danger,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '3px',
      textShadow: `0 0 6px ${COLORS.danger}`,
      transition: 'all 0.2s',
    });
    this.cancelBtn.textContent = t('warpGate.cancel');
    this.cancelBtn.addEventListener('mouseenter', () => {
      this.cancelBtn.style.background = COLORS.danger;
      this.cancelBtn.style.color = '#000';
    });
    this.cancelBtn.addEventListener('mouseleave', () => {
      this.cancelBtn.style.background = 'transparent';
      this.cancelBtn.style.color = COLORS.danger;
    });
    this.cancelBtn.addEventListener('click', () => {
      this.hide();
      this.onCloseCb();
    });
    header.appendChild(this.cancelBtn);
    this.root.appendChild(header);

    attachMobileCloseButton(
      this.root,
      () => {
        this.hide();
        this.onCloseCb();
      },
      { hideOnMobile: [this.cancelBtn] },
    );

    const body = this.el('div', {
      display: 'flex',
      flex: '1',
      minHeight: '0',
      position: 'relative',
    });
    this.root.appendChild(body);

    this.canvas = document.createElement('canvas');
    Object.assign(this.canvas.style, {
      flex: '1',
      display: 'block',
      cursor: 'grab',
      touchAction: 'none',
    });
    body.appendChild(this.canvas);

    this.buildListView(body);
    this.buildViewToggle(body);
    this.buildInfoPanel(body);

    this.attachCanvasEvents();
  }

  /**
   * Floating two-button toggle anchored to the top-left of the body so
   * pilots can flip between the 3D hologram and a flat sector list at
   * any time. The list is more useful when the discovered set is huge
   * or when the desired sector lies outside the currently visible
   * portion of the 3D cloud (e.g. ±VIEW_RADIUS away from current).
   */
  private buildViewToggle(parent: HTMLElement): void {
    const mobile = isMobileLayout();
    this.viewToggleBar = this.el('div', {
      position: 'absolute',
      top: mobile ? '8px' : '16px',
      left: mobile ? '8px' : '16px',
      display: 'flex',
      gap: '0',
      background: COLORS.panelBg,
      border: `1px solid ${COLORS.dim}`,
      pointerEvents: 'auto',
      zIndex: '3',
    });
    parent.appendChild(this.viewToggleBar);

    const mkBtn = (label: string): HTMLButtonElement => {
      const b = document.createElement('button');
      Object.assign(b.style, {
        fontFamily: FONT,
        fontSize: mobile ? '10px' : '11px',
        padding: mobile ? '5px 10px' : '6px 14px',
        background: 'transparent',
        border: 'none',
        color: COLORS.primary,
        cursor: 'pointer',
        letterSpacing: '2px',
        textShadow: GLOW,
      } as Partial<CSSStyleDeclaration>);
      b.textContent = label;
      return b;
    };

    this.viewMapBtn = mkBtn(t('warpGate.view3d'));
    this.viewListBtn = mkBtn(t('warpGate.viewList'));
    this.viewMapBtn.addEventListener('click', () => this.setViewMode('map'));
    this.viewListBtn.addEventListener('click', () => this.setViewMode('list'));
    this.viewToggleBar.appendChild(this.viewMapBtn);
    this.viewToggleBar.appendChild(this.viewListBtn);
  }

  private setViewMode(mode: 'map' | 'list'): void {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    this.applyViewMode();
    if (mode === 'list') this.renderSectorList();
  }

  private applyViewMode(): void {
    const isMap = this.viewMode === 'map';
    this.canvas.style.display = isMap ? 'block' : 'none';
    this.listContainer.style.display = isMap ? 'none' : 'flex';
    const onColor = COLORS.current;
    const onShadow = CURRENT_GLOW;
    const offColor = COLORS.primary;
    const offShadow = GLOW;
    this.viewMapBtn.style.color = isMap ? onColor : offColor;
    this.viewMapBtn.style.textShadow = isMap ? onShadow : offShadow;
    this.viewMapBtn.style.background = isMap ? 'rgba(0,255,65,0.08)' : 'transparent';
    this.viewListBtn.style.color = !isMap ? onColor : offColor;
    this.viewListBtn.style.textShadow = !isMap ? onShadow : offShadow;
    this.viewListBtn.style.background = !isMap ? 'rgba(0,255,65,0.08)' : 'transparent';
  }

  /**
   * Sortable, click-to-select list of every known sector — the
   * accessibility-friendly counterpart to the 3D cloud. Uses the same
   * `select(coord)` path so the right-hand info / cost / travel panel
   * stays in sync regardless of which view picked the sector.
   */
  private buildListView(parent: HTMLElement): void {
    const mobile = isMobileLayout();
    this.listContainer = this.el('div', {
      position: 'absolute',
      // Leave room for the view-toggle bar (top-left) and the info
      // panel (top-right); the list fills the remaining canvas area.
      top: mobile ? '44px' : '60px',
      left: mobile ? '8px' : '16px',
      bottom: mobile ? '8px' : '16px',
      // The info panel hugs the right; we cap our width so we don't
      // run under it.
      right: mobile ? 'calc(min(40vw, 220px) + 18px)' : 'clamp(240px, 26vw, 340px)',
      display: 'none',
      flexDirection: 'column',
      gap: '8px',
      background: COLORS.panelBg,
      border: `1px solid ${COLORS.dim}`,
      padding: mobile ? '8px 10px' : '12px 14px',
      pointerEvents: 'auto',
      zIndex: '2',
      overflow: 'hidden',
    });
    parent.appendChild(this.listContainer);
  }

  /** Rebuild the sector list rows from the current discovered set. */
  private renderSectorList(): void {
    const mobile = isMobileLayout();
    this.listContainer.innerHTML = '';

    const headerRow = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      paddingBottom: '6px',
      borderBottom: `1px solid ${COLORS.dim}`,
    });
    headerRow.appendChild(this.el('div', {
      fontSize: mobile ? '11px' : '13px',
      letterSpacing: '3px',
      textShadow: GLOW,
    }, t('warpGate.listTitle')));
    headerRow.appendChild(this.el('div', {
      fontSize: '9px',
      color: 'rgba(0,204,255,0.55)',
      letterSpacing: '1px',
    }, t('warpGate.listCount', { n: this.discovered.length.toString() })));
    this.listContainer.appendChild(headerRow);

    const scroller = this.el('div', {
      flex: '1',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      paddingRight: '2px',
    });
    this.listContainer.appendChild(scroller);

    if (this.discovered.length === 0) {
      scroller.appendChild(this.el('div', {
        fontSize: '11px',
        color: 'rgba(0,204,255,0.5)',
        textAlign: 'center',
        padding: '20px 8px',
        textTransform: 'none',
      }, t('warpGate.listEmpty')));
      return;
    }

    // Sort: current sector first, then warp-gate sectors sorted by
    // travel cost ascending, then non-gate known sectors at the bottom.
    const currentKey = sectorKey(this.current);
    const rows = this.discovered.slice().sort((a, b) => {
      const aCur = sectorKey(a) === currentKey;
      const bCur = sectorKey(b) === currentKey;
      if (aCur !== bCur) return aCur ? -1 : 1;
      const aGate = sectorHasWarpGate(a);
      const bGate = sectorHasWarpGate(b);
      if (aGate !== bGate) return aGate ? -1 : 1;
      const aCost = aGate ? warpTravelCostFor(this.current, a, this.hasPremium) : Number.MAX_SAFE_INTEGER;
      const bCost = bGate ? warpTravelCostFor(this.current, b, this.hasPremium) : Number.MAX_SAFE_INTEGER;
      if (aCost !== bCost) return aCost - bCost;
      return sectorName(a).localeCompare(sectorName(b));
    });

    for (const s of rows) {
      scroller.appendChild(this.buildListRow(s));
    }
  }

  private buildListRow(s: SectorCoord): HTMLElement {
    const mobile = isMobileLayout();
    const key = sectorKey(s);
    const isCurrent = key === sectorKey(this.current);
    const isSelected = !!this.selected && sectorKey(this.selected) === key;
    const hasGate = sectorHasWarpGate(s);
    const isOrigin = s.x === 0 && s.y === 0 && s.z === 0;

    const row = this.el('div', {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      alignItems: 'center',
      gap: '8px',
      padding: mobile ? '6px 8px' : '8px 10px',
      border: `1px solid ${
        isSelected ? COLORS.primary : isCurrent ? COLORS.current : COLORS.dim
      }`,
      background: isSelected ? 'rgba(0,204,255,0.10)' : 'rgba(0,8,24,0.6)',
      cursor: 'pointer',
      textTransform: 'none',
      letterSpacing: '0',
    });

    const text = this.el('div', { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '0' });
    const titleColor = isOrigin
      ? COLORS.origin
      : isCurrent ? COLORS.current : hasGate ? COLORS.primary : COLORS.noGate;
    const titleEl = this.el('div', {
      fontSize: mobile ? '11px' : '13px',
      color: titleColor,
      textShadow: hasGate || isCurrent || isOrigin ? GLOW : 'none',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      letterSpacing: '1px',
    }, sectorName(s));
    text.appendChild(titleEl);

    const tags: string[] = [`[${s.x}, ${s.y}, ${s.z}]`];
    if (isOrigin) tags.push(t('sector.galacticCenterHint'));
    if (isCurrent) tags.push(t('warpGate.listTagCurrent'));
    else if (!hasGate) tags.push(t('warpGate.listTagNoGate'));
    else if (this.isRangeLocked(s)) tags.push(t('warpGate.listTagPremium'));
    text.appendChild(this.el('div', {
      fontSize: '9px',
      color: 'rgba(0,204,255,0.55)',
      letterSpacing: '1px',
    }, tags.join(' · ')));

    row.appendChild(text);

    const rangeLocked = !isCurrent && hasGate && this.isRangeLocked(s);
    const right = this.el('div', {
      fontSize: mobile ? '10px' : '11px',
      color: hasGate && !isCurrent ? COLORS.secondary : 'rgba(0,204,255,0.45)',
      whiteSpace: 'nowrap',
    }, isCurrent
      ? t('warpGate.currentSector')
      : rangeLocked
        ? `🔒 ${t('warpGate.listTagPremiumShort')}`
        : hasGate
          ? `${warpTravelCostFor(this.current, s, this.hasPremium).toLocaleString()}C`
          : t('warpGate.listTagNoGateShort'));
    row.appendChild(right);

    row.addEventListener('click', () => this.select(s));
    return row;
  }

  private buildInfoPanel(parent: HTMLElement): void {
    // Mobile squeezes the side panel so the 3D gate cloud isn't
    // pushed off-screen; desktop keeps the roomier original sizing.
    const mobile = isMobileLayout();
    this.infoPanel = this.el('div', {
      position: 'absolute',
      top: mobile ? '8px' : '16px',
      right: mobile ? '8px' : '16px',
      width: mobile ? 'min(40vw, 220px)' : 'clamp(220px, 24vw, 320px)',
      background: COLORS.panelBg,
      border: `1px solid ${COLORS.dim}`,
      boxShadow: `0 0 12px rgba(0,204,255,0.2)`,
      padding: mobile ? '10px 12px' : '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: mobile ? '6px' : '10px',
      pointerEvents: 'auto',
      fontSize: mobile ? '10px' : '',
      maxHeight: mobile ? 'calc(100% - 72px)' : '',
      overflow: mobile ? 'auto' : '',
    });
    parent.appendChild(this.infoPanel);

    const selectedHeader = this.el('div', {
      fontSize: '9px',
      color: 'rgba(0,204,255,0.55)',
      letterSpacing: '3px',
    }, t('warpGate.selected'));
    this.infoPanel.appendChild(selectedHeader);

    this.selectedInfoEl = this.el('div', {
      fontSize: mobile ? '13px' : '18px',
      textShadow: GLOW,
      letterSpacing: mobile ? '1px' : '2px',
      minHeight: mobile ? '16px' : '22px',
    }, '—');
    this.infoPanel.appendChild(this.selectedInfoEl);

    this.selectedOwnerEl = this.el('div', {
      fontSize: mobile ? '9px' : '10px',
      color: 'rgba(0,204,255,0.75)',
      letterSpacing: '1px',
      minHeight: mobile ? '12px' : '14px',
      textTransform: 'none',
    }, '');
    this.infoPanel.appendChild(this.selectedOwnerEl);

    // ─── Zone tier label + flavour + NPC summary ───
    // The tier label is a single short word (CONTESTED / EMBATTLED / …).
    // The flavour line wraps freely so the deep-tier descriptions stay
    // readable. The NPC summary feeds off the same deterministic
    // generator the server uses, so the numbers shown here are exactly
    // what will spawn the moment the pilot warps in.
    this.selectedZoneEl = this.el('div', {
      fontSize: mobile ? '9px' : '11px',
      letterSpacing: mobile ? '1px' : '2px',
      marginTop: '4px',
    }, '');
    this.infoPanel.appendChild(this.selectedZoneEl);

    this.selectedZoneDescEl = this.el('div', {
      fontSize: mobile ? '9px' : '10px',
      color: 'rgba(0,204,255,0.65)',
      lineHeight: '1.45',
      letterSpacing: '0',
      whiteSpace: 'normal',
      textTransform: 'none',
      margin: '2px 0',
    }, '');
    this.infoPanel.appendChild(this.selectedZoneDescEl);

    this.selectedNpcSummaryEl = this.el('div', {
      fontSize: mobile ? '9px' : '10px',
      color: 'rgba(0,204,255,0.7)',
      letterSpacing: '1px',
      textTransform: 'none',
    }, '');
    this.infoPanel.appendChild(this.selectedNpcSummaryEl);

    // Active world-event card. Hidden by default; revealed when the
    // pilot selects a sector that has a running mothership alert,
    // raid, or skirmish so they can read what's happening before
    // committing to a warp.
    this.eventInfoEl = this.el('div', {
      display: 'none',
      flexDirection: 'column',
      gap: '4px',
      marginTop: '4px',
      padding: mobile ? '6px 8px' : '8px 10px',
      border: `1px solid ${COLORS.eventInfo}`,
      background: 'rgba(255,176,0,0.08)',
      boxShadow: `0 0 8px rgba(255,176,0,0.15)`,
      textTransform: 'none',
      letterSpacing: '0',
    });
    this.infoPanel.appendChild(this.eventInfoEl);

    const divider = this.el('div', {
      height: '1px',
      background: COLORS.dim,
      margin: '4px 0',
    });
    this.infoPanel.appendChild(divider);

    this.costEl = this.el('div', {
      fontSize: mobile ? '10px' : '12px',
      color: COLORS.secondary,
      textShadow: `0 0 6px ${COLORS.secondary}`,
      minHeight: mobile ? '14px' : '16px',
    }, '—');
    this.infoPanel.appendChild(this.costEl);

    this.errorEl = this.el('div', {
      fontSize: mobile ? '9px' : '10px',
      color: COLORS.danger,
      textShadow: `0 0 6px ${COLORS.danger}`,
      minHeight: mobile ? '12px' : '14px',
    }, '');
    this.infoPanel.appendChild(this.errorEl);

    this.travelBtn = document.createElement('button');
    Object.assign(this.travelBtn.style, {
      fontFamily: FONT,
      fontSize: mobile ? '11px' : '14px',
      padding: mobile ? '7px 12px' : '10px 18px',
      background: 'transparent',
      border: `2px solid ${COLORS.primary}`,
      color: COLORS.primary,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: mobile ? '2px' : '4px',
      textShadow: GLOW,
      transition: 'all 0.2s',
      marginTop: '4px',
    });
    this.travelBtn.textContent = t('warpGate.travel');
    this.travelBtn.addEventListener('mouseenter', () => {
      if (this.travelBtn.disabled) return;
      this.travelBtn.style.background = COLORS.primary;
      this.travelBtn.style.color = '#000';
    });
    this.travelBtn.addEventListener('mouseleave', () => {
      this.travelBtn.style.background = 'transparent';
      this.travelBtn.style.color = COLORS.primary;
    });
    this.travelBtn.addEventListener('click', () => {
      if (this.selected && !this.travelBtn.disabled) {
        this.onTravelCb(this.selected);
      }
    });
    this.infoPanel.appendChild(this.travelBtn);

    // Premium upsell CTA — surfaces when a free pilot selects a
    // destination beyond FREE_WARP_MAX_JUMP. Gold to match the rest
    // of the premium visual language.
    this.upgradeBtn = document.createElement('button');
    Object.assign(this.upgradeBtn.style, {
      fontFamily: FONT,
      fontSize: mobile ? '10px' : '12px',
      padding: mobile ? '7px 12px' : '9px 16px',
      background: 'rgba(255,176,0,0.1)',
      border: `1px solid ${COLORS.secondary}`,
      color: COLORS.secondary,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: mobile ? '1px' : '2px',
      textShadow: `0 0 6px rgba(255,176,0,0.4)`,
      transition: 'all 0.2s',
      marginTop: '4px',
      display: 'none',
    });
    this.upgradeBtn.textContent = t('warpGate.premiumUnlockCta');
    this.upgradeBtn.addEventListener('mouseenter', () => {
      this.upgradeBtn.style.background = COLORS.secondary;
      this.upgradeBtn.style.color = '#000';
    });
    this.upgradeBtn.addEventListener('mouseleave', () => {
      this.upgradeBtn.style.background = 'rgba(255,176,0,0.1)';
      this.upgradeBtn.style.color = COLORS.secondary;
    });
    this.upgradeBtn.addEventListener('click', () => {
      this.onUpgradeCb?.();
    });
    this.infoPanel.appendChild(this.upgradeBtn);

    const hint = this.el('div', {
      fontSize: '9px',
      color: 'rgba(0,204,255,0.4)',
      letterSpacing: '1px',
      marginTop: '8px',
      textTransform: 'none',
      // Desktop-only keybind hint — hidden on touch devices.
      display: mobile ? 'none' : '',
    }, 'Drag to rotate • Scroll to zoom • Click sector to target');
    this.infoPanel.appendChild(hint);

    this.updateInfoPanel();
  }

  private attachCanvasEvents(): void {
    this.canvas.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    this.canvas.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      this.pointerDown = { x: e.clientX, y: e.clientY, time: performance.now() };
      this.dragging = false;
      this.canvas.style.cursor = 'grabbing';
    });

    this.canvas.addEventListener('pointermove', (e) => {
      if (!this.pointerDown) return;
      const dx = e.clientX - this.pointerDown.x;
      const dy = e.clientY - this.pointerDown.y;
      if (!this.dragging && (dx * dx + dy * dy) > 9) {
        this.dragging = true;
      }
      if (this.dragging) {
        this.rotY += dx * 0.005;
        this.rotX = Math.max(-1.2, Math.min(1.2, this.rotX + dy * 0.005));
        this.pointerDown.x = e.clientX;
        this.pointerDown.y = e.clientY;
      }
    });

    this.canvas.addEventListener('pointerup', (e) => {
      const wasDragging = this.dragging;
      this.pointerDown = null;
      this.dragging = false;
      this.canvas.style.cursor = 'grab';
      if (!wasDragging) {
        this.handleCanvasClick(e);
      }
    });

    this.canvas.addEventListener('pointercancel', () => {
      this.pointerDown = null;
      this.dragging = false;
      this.canvas.style.cursor = 'grab';
    });

    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 1.1 : 1 / 1.1;
      this.zoom = Math.max(0.3, Math.min(4, this.zoom * factor));
    }, { passive: false });
  }

  private handleCanvasClick(e: PointerEvent): void {
    if (!this.renderer) return;
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);
    const meshes = Array.from(this.sectorMeshes.values());
    const hits = this.raycaster.intersectObjects(meshes, false);
    if (hits.length === 0) {
      this.select(null);
      return;
    }
    const hit = hits[0].object as THREE.Mesh;
    const coord = hit.userData['coord'] as SectorCoord | undefined;
    if (coord) this.select(coord);
  }

  private ensureRenderer(): void {
    if (this.renderer) return;
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000011, 0);

    this.camera = new THREE.PerspectiveCamera(55, 1, 0.1, 1000);
    this.camera.position.set(0, 5, 18);
    this.camera.lookAt(0, 0, 0);

    const ambient = new THREE.AmbientLight(0x334466, 0.6);
    this.scene.add(ambient);
    const dir = new THREE.DirectionalLight(0x88ccff, 0.8);
    dir.position.set(5, 10, 5);
    this.scene.add(dir);

    this.addGridFloor();
  }

  private addGridFloor(): void {
    const grid = new THREE.GridHelper(40, 20, 0x0077aa, 0x004466);
    (grid.material as THREE.Material).transparent = true;
    (grid.material as THREE.Material).opacity = 0.4;
    grid.position.y = -4;
    this.scene.add(grid);
  }

  private rebuildHologram(): void {
    for (const mesh of this.sectorMeshes.values()) {
      this.hologramRoot.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.sectorMeshes.clear();

    for (const line of this.linkLines) {
      this.hologramRoot.remove(line);
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    }
    this.linkLines = [];

    const SCALE = 2.5;

    const currentKey = sectorKey(this.current);
    const uniqueKeys = new Set<string>();
    const sectors: SectorCoord[] = [];
    for (const s of this.discovered) {
      const k = sectorKey(s);
      if (uniqueKeys.has(k)) continue;
      uniqueKeys.add(k);
      sectors.push(s);
    }
    if (!uniqueKeys.has(currentKey)) {
      sectors.push(this.current);
    }

    for (const s of sectors) {
      const key = sectorKey(s);
      const isCurrent = key === currentKey;
      const isSelected = this.selected && sectorKey(this.selected) === key;
      const hasGate = sectorHasWarpGate(s);
      const isOrigin = s.x === 0 && s.y === 0 && s.z === 0;

      const baseColor = isCurrent
        ? COLORS.currentNum
        : hasGate
          ? COLORS.primaryNum
          : COLORS.noGateNum;

      // Subtle race hint: only on "lit" nodes (warp-gate sectors + the
      // player's current sector) so dim "no-gate" dots stay inert.
      // Skip on the current sector so it keeps its pure green "you are
      // here" reading.
      const showOwnerHint = hasGate && !isCurrent;
      const ownerTint = showOwnerHint
        ? this.ownerTintNum(s, this.claimByKey.get(key))
        : baseColor;
      const emissiveColor = showOwnerHint
        ? this.blendHex(baseColor, ownerTint, 0.3)
        : baseColor;

      const geo = new THREE.SphereGeometry(0.45, 24, 24);
      const mat = new THREE.MeshStandardMaterial({
        color: baseColor,
        emissive: emissiveColor,
        emissiveIntensity: isSelected ? 1.2 : hasGate || isCurrent ? 0.6 : 0.15,
        metalness: 0.2,
        roughness: hasGate || isCurrent ? 0.35 : 0.7,
        transparent: true,
        opacity: hasGate || isCurrent ? 0.95 : 0.55,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(s.x * SCALE, s.y * SCALE, s.z * SCALE);
      mesh.userData['coord'] = s;
      this.hologramRoot.add(mesh);
      this.sectorMeshes.set(key, mesh);

      // Outer glow ring — only drawn for sectors that actually host a
      // warp gate (or the player's current sector), so the grey "no
      // gate" nodes read as inert points on the map.
      if (hasGate || isCurrent) {
        const ringGeo = new THREE.RingGeometry(0.65, 0.8, 32);
        const ringMat = new THREE.MeshBasicMaterial({
          color: isCurrent ? COLORS.currentNum : isSelected ? 0xffffff : COLORS.primaryNum,
          transparent: true,
          opacity: isSelected ? 0.9 : 0.4,
          side: THREE.DoubleSide,
          depthWrite: false,
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.position.copy(mesh.position);
        mesh.add(ring);
        ring.position.set(0, 0, 0);
        ring.userData['isRing'] = true;
      }

      // Active world-event ring. Painted on every known sector with a
      // running event (with or without a gate) so pilots can spot
      // hot zones at a glance even when the destination is dim.
      const events = this.eventsByKey.get(key);
      if (events && events.length > 0) {
        const colorNum = this.eventRingColorNum(events);
        const ringGeo = new THREE.RingGeometry(0.95, 1.12, 36);
        const ringMat = new THREE.MeshBasicMaterial({
          color: colorNum,
          transparent: true,
          opacity: 0.85,
          side: THREE.DoubleSide,
          depthWrite: false,
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        mesh.add(ring);
        ring.position.set(0, 0, 0);
        ring.userData['isRing'] = true;
        ring.userData['eventRing'] = true;
      }

      // Galactic center marker — gold double-ring + tumbling
      // octahedron so the origin sector reads as the prime landmark
      // of the warp-gate network independent of selection / event /
      // ownership state.
      if (isOrigin) {
        const halo = new THREE.Mesh(
          new THREE.RingGeometry(0.55, 0.7, 32),
          new THREE.MeshBasicMaterial({
            color: COLORS.originNum,
            transparent: true,
            opacity: 0.85,
            side: THREE.DoubleSide,
            depthWrite: false,
          }),
        );
        halo.position.set(0, 0, 0);
        halo.userData['isRing'] = true;
        halo.userData['originRing'] = true;
        mesh.add(halo);

        const outer = new THREE.Mesh(
          new THREE.RingGeometry(1.25, 1.4, 36),
          new THREE.MeshBasicMaterial({
            color: COLORS.originNum,
            transparent: true,
            opacity: 0.5,
            side: THREE.DoubleSide,
            depthWrite: false,
          }),
        );
        outer.position.set(0, 0, 0);
        outer.userData['isRing'] = true;
        outer.userData['originRing'] = true;
        mesh.add(outer);

        const star = new THREE.Mesh(
          new THREE.OctahedronGeometry(0.22, 0),
          new THREE.MeshStandardMaterial({
            color: COLORS.originNum,
            emissive: COLORS.originNum,
            emissiveIntensity: 1.4,
            metalness: 0.4,
            roughness: 0.2,
          }),
        );
        star.position.set(0, 0, 0);
        star.userData['originStar'] = true;
        mesh.add(star);
      }
    }

    if (currentKey) {
      const currentMesh = this.sectorMeshes.get(currentKey);
      if (currentMesh) {
        for (const s of sectors) {
          const key = sectorKey(s);
          if (key === currentKey) continue;
          const dest = this.sectorMeshes.get(key);
          if (!dest) continue;
          const destHasGate = sectorHasWarpGate(s);
          const lineGeo = new THREE.BufferGeometry().setFromPoints([
            currentMesh.position.clone(),
            dest.position.clone(),
          ]);
          const lineMat = new THREE.LineBasicMaterial({
            color: destHasGate ? COLORS.dimNum : COLORS.noGateNum,
            transparent: true,
            opacity: destHasGate ? 0.35 : 0.12,
          });
          const line = new THREE.Line(lineGeo, lineMat);
          this.hologramRoot.add(line);
          this.linkLines.push(line);
        }
      }
    }

    // Recenter group around current sector
    if (this.sectorMeshes.has(currentKey)) {
      const center = this.sectorMeshes.get(currentKey)!.position.clone();
      this.hologramRoot.position.copy(center.clone().negate());
    } else {
      this.hologramRoot.position.set(0, 0, 0);
    }
  }

  private select(coord: SectorCoord | null): void {
    // We keep the current sector as a valid selection target so that
    // clicking it shows "CURRENT SECTOR" in the info panel instead of
    // silently clearing — previously the whole panel reset to "—"
    // which looked like the click had done nothing.
    this.selected = coord ?? null;
    this.rebuildHologram();
    this.updateInfoPanel();
    if (this.viewMode === 'list') this.renderSectorList();
  }

  /**
   * Who owns (or lore-holds) a sector, formatted for the right panel.
   *
   * Falls back to the deterministic `dominantRace(coord)` so the line
   * is never blank — NPC-held sectors show `◇ Dominant: Race`, frontier
   * sectors show `★ Unclaimed frontier`, real player claims win with ●.
   */
  private ownershipSubtitle(coord: SectorCoord, claim: SectorClaim | undefined): string {
    if (claim?.ownerPlayerId) {
      const race = claim.ownerRace != null ? RACE_INFO[claim.ownerRace] : undefined;
      return `● ${claim.ownerName ?? '?'}${race ? ` · ${race.name}` : ''}`;
    }
    const dom = dominantRace(coord);
    if (dom != null) {
      return `◇ ${t('ownership.dominant', { race: RACE_INFO[dom].name })}`;
    }
    return `★ ${t('ownership.unclaimed')}`;
  }

  /** Race primary color for the sector's owner/dominant race; white for frontier. */
  private ownerTintNum(coord: SectorCoord, claim: SectorClaim | undefined): number {
    if (claim?.ownerRace != null) return RACE_INFO[claim.ownerRace].primaryColor;
    const dom = dominantRace(coord);
    if (dom != null) return RACE_INFO[dom].primaryColor;
    return 0xffffff;
  }

  /** Linear blend of two hex colors (0xRRGGBB), weighted `t` toward `b`. */
  private blendHex(a: number, b: number, tt: number): number {
    const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
    const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
    const r = Math.round(ar + (br - ar) * tt);
    const g = Math.round(ag + (bg - ag) * tt);
    const bl = Math.round(ab + (bb - ab) * tt);
    return (r << 16) | (g << 8) | bl;
  }

  /**
   * Render a one-line "X hostile / Y neutral / …" preview for a sector
   * by tallying the deterministic NPC pool. Empty buckets are elided so
   * the line stays tidy on Luminara worlds (all friendly), trade hubs
   * (all neutral), or pirate dens (all hostile).
   */
  private formatNpcSummary(summary: SectorNPCSummary): string {
    if (summary.total === 0) return t('npcSummary.empty');
    const parts: string[] = [];
    if (summary.hostile > 0) parts.push(t('npcSummary.hostile', { count: summary.hostile }));
    if (summary.neutral > 0) parts.push(t('npcSummary.neutral', { count: summary.neutral }));
    if (summary.friendly > 0) parts.push(t('npcSummary.friendly', { count: summary.friendly }));
    if (summary.unknown > 0) parts.push(t('npcSummary.unknown', { count: summary.unknown }));
    return parts.join(' · ');
  }

  private updateInfoPanel(): void {
    if (!this.selected) {
      this.selectedInfoEl.textContent = '—';
      this.selectedInfoEl.style.color = 'rgba(0,204,255,0.55)';
      this.selectedInfoEl.style.textShadow = 'none';
      this.selectedOwnerEl.textContent = '';
      this.selectedOwnerEl.style.color = 'rgba(0,204,255,0.75)';
      this.selectedZoneEl.textContent = '';
      this.selectedZoneDescEl.textContent = '';
      this.selectedZoneDescEl.style.display = 'none';
      this.selectedNpcSummaryEl.textContent = '';
      this.renderEventInfo([]);
      this.costEl.textContent = '—';
      this.errorEl.textContent =
        this.discovered.length <= 1 ? t('warpGate.onlyOne') : '';
      this.upgradeBtn.style.display = 'none';
      this.disableTravel();
      return;
    }

    const coord = this.selected;
    const isCurrent = sectorKey(coord) === sectorKey(this.current);
    const destHasGate = sectorHasWarpGate(coord);
    const isOrigin = coord.x === 0 && coord.y === 0 && coord.z === 0;

    // ─── Zone tier display ───
    const diff = getZoneDifficulty(coord);
    const tierIdx = Math.max(0, Math.min(diff - 1, ZONE_TIER_KEYS.length - 1));
    const tierKey = ZONE_TIER_KEYS[tierIdx]!;
    this.selectedZoneEl.textContent = `${t('universeMap.zone')}: ${t(`zone.${tierKey}`)}`;
    this.selectedZoneEl.style.color = WARPGATE_TIER_COLORS[tierKey] ?? COLORS.primary;
    const desc = t(`zone.${tierKey}.desc`);
    this.selectedZoneDescEl.textContent = desc;
    this.selectedZoneDescEl.style.display = desc ? 'block' : 'none';

    // ─── NPC disposition breakdown ───
    const npcSummary = summarizeSectorNPCs(coord);
    this.selectedNpcSummaryEl.textContent = `${t('npcSummary.title')}: ${this.formatNpcSummary(npcSummary)}`;
    this.selectedNpcSummaryEl.style.color =
      npcSummary.hostile > npcSummary.neutral + npcSummary.friendly
        ? COLORS.danger
        : 'rgba(0,204,255,0.7)';

    const name = sectorName(coord);
    const claim = this.claimByKey.get(sectorKey(coord));
    const ownLine = this.ownershipSubtitle(coord, claim);
    // Prepend the gold "Galactic Center" tag so the origin sector
    // surfaces its identity even before reading the title line.
    this.selectedOwnerEl.textContent = isOrigin
      ? `${t('sector.galacticCenterHint')} · ${ownLine}`
      : ownLine;
    this.selectedOwnerEl.style.color = isOrigin
      ? COLORS.origin
      : 'rgba(0,204,255,0.75)';
    this.renderEventInfo(this.eventsByKey.get(sectorKey(coord)) ?? []);
    if (isCurrent) {
      this.selectedInfoEl.textContent =
        `${t('warpGate.currentSector')} — ${name} [${coord.x}, ${coord.y}, ${coord.z}]`;
      this.selectedInfoEl.style.color = isOrigin ? COLORS.origin : COLORS.current;
      this.selectedInfoEl.style.textShadow = isOrigin
        ? `0 0 8px ${COLORS.origin}, 0 0 16px rgba(255,215,0,0.35)`
        : CURRENT_GLOW;
      this.costEl.textContent = t('warpGate.currentSectorHint');
      this.errorEl.textContent = '';
      this.upgradeBtn.style.display = 'none';
      this.disableTravel();
      return;
    }

    this.selectedInfoEl.textContent =
      `${name} [${coord.x}, ${coord.y}, ${coord.z}]`;
    this.selectedInfoEl.style.color = isOrigin
      ? COLORS.origin
      : destHasGate ? COLORS.primary : COLORS.noGate;
    this.selectedInfoEl.style.textShadow = isOrigin
      ? `0 0 8px ${COLORS.origin}, 0 0 16px rgba(255,215,0,0.35)`
      : destHasGate ? GLOW : 'none';

    const cost = warpTravelCostFor(this.current, coord, this.hasPremium);
    this.costEl.textContent = `${t('warpGate.cost')}: ${cost.toLocaleString()}C`;

    this.upgradeBtn.style.display = 'none';
    if (!destHasGate) {
      // Destination sector has no warp gate — players have to fly there
      // manually. Explicit feedback keeps them from clicking a dead button.
      this.errorEl.textContent = t('warpGate.noGateAtDestination');
      this.disableTravel();
    } else if (this.isRangeLocked(coord)) {
      // Beyond the free-account jump range. The pilot can still get
      // there by chaining shorter hops through intermediate gates;
      // Premium unlocks the direct jump. The CTA opens the upgrade
      // modal so the friction point doubles as the upsell surface.
      this.errorEl.textContent = t('warpGate.premiumRange', {
        max: String(FREE_WARP_MAX_JUMP),
      });
      this.disableTravel();
      if (this.onUpgradeCb) this.upgradeBtn.style.display = 'block';
    } else if (this.credits < cost) {
      this.errorEl.textContent = t('warpGate.insufficient');
      this.disableTravel();
    } else {
      this.errorEl.textContent = '';
      this.enableTravel();
    }
  }

  private disableTravel(): void {
    this.travelBtn.disabled = true;
    this.travelBtn.style.opacity = '0.35';
    this.travelBtn.style.cursor = 'not-allowed';
    this.travelBtn.style.background = 'transparent';
    this.travelBtn.style.color = COLORS.primary;
  }

  private enableTravel(): void {
    this.travelBtn.disabled = false;
    this.travelBtn.style.opacity = '1';
    this.travelBtn.style.cursor = 'pointer';
  }

  private resizeRenderer(): void {
    if (!this.renderer) return;
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, rect.width);
    const h = Math.max(1, rect.height);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private animate = (): void => {
    if (!this.visible) return;
    this.animFrame = requestAnimationFrame(this.animate);

    this.time += 0.016;
    this.resizeRenderer();

    // Apply rotation + zoom to camera
    const radius = 18 / this.zoom;
    const cosX = Math.cos(this.rotX);
    const sinX = Math.sin(this.rotX);
    const cosY = Math.cos(this.rotY);
    const sinY = Math.sin(this.rotY);
    this.camera.position.set(
      radius * cosX * sinY,
      radius * sinX,
      radius * cosX * cosY,
    );
    this.camera.lookAt(0, 0, 0);

    // Pulse selected sector
    if (this.selected) {
      const key = sectorKey(this.selected);
      const mesh = this.sectorMeshes.get(key);
      if (mesh) {
        const pulse = 1 + Math.sin(this.time * 4) * 0.15;
        mesh.scale.setScalar(pulse);
      }
    }

    // Slowly rotate all rings
    for (const mesh of this.sectorMeshes.values()) {
      mesh.children.forEach((c) => {
        if (c.userData['originStar']) {
          c.rotation.x += 0.02;
          c.rotation.y += 0.025;
          c.rotation.z += 0.012;
          return;
        }
        if (!c.userData['isRing']) return;
        if (c.userData['eventRing']) {
          c.rotation.z += 0.022;
          const m = (c as THREE.Mesh).material as THREE.MeshBasicMaterial;
          m.opacity = 0.55 + 0.35 * (0.5 + 0.5 * Math.sin(this.time * 3));
        } else if (c.userData['originRing']) {
          c.rotation.z += 0.018;
          const m = (c as THREE.Mesh).material as THREE.MeshBasicMaterial;
          m.opacity = 0.55 + 0.3 * (0.5 + 0.5 * Math.sin(this.time * 2));
        } else {
          c.rotation.z += 0.01;
        }
      });
    }

    this.tickEventCountdowns();

    this.renderer!.render(this.scene, this.camera);
  };

  /**
   * Refresh the per-event countdown labels in the info card so the
   * text stays accurate as time passes. Cheap because there's at most
   * a handful of events on the selected sector.
   */
  private tickEventCountdowns(): void {
    if (this.eventInfoEl.style.display === 'none') return;
    const now = Date.now();
    const labels = this.eventInfoEl.querySelectorAll<HTMLElement>('[data-event-ends-at]');
    labels.forEach((el) => {
      const ends = Number(el.dataset['eventEndsAt'] ?? 0);
      if (!ends) return;
      el.textContent = this.formatEventCountdown(ends, now);
    });
  }

  show(params: {
    current: SectorCoord;
    discovered: SectorCoord[];
    credits: number;
    claims?: SectorClaim[];
    activeEvents?: ActiveSectorEvent[];
  }): void {
    this.current = { ...params.current };
    this.discovered = params.discovered.map((d) => ({ ...d }));
    this.credits = params.credits;
    this.syncClaims(params.claims);
    this.syncActiveEvents(params.activeEvents);
    this.selected = null;

    this.ensureRenderer();
    this.root.style.display = 'flex';
    this.visible = true;
    this.rebuildHologram();
    this.updateInfoPanel();
    this.applyViewMode();
    if (this.viewMode === 'list') this.renderSectorList();
    this.resizeRenderer();
    this.animate();
  }

  private syncClaims(claims: SectorClaim[] | undefined): void {
    this.claimByKey.clear();
    if (!claims) return;
    for (const c of claims) this.claimByKey.set(c.sectorKey, c);
  }

  private syncActiveEvents(events: ActiveSectorEvent[] | undefined): void {
    this.eventsByKey.clear();
    if (!events) return;
    for (const ev of events) {
      const key = sectorKey(ev.sector);
      const list = this.eventsByKey.get(key);
      if (list) list.push(ev);
      else this.eventsByKey.set(key, [ev]);
    }
  }

  /**
   * Severity → tint resolution for the active-event ring + info card.
   * Returns the worst severity in the list so a stack of events on the
   * same sector advertises the scariest one at a glance.
   */
  private eventRingColorNum(events: ActiveSectorEvent[]): number {
    let worst: ActiveSectorEvent['level'] = 'info';
    const rank: Record<ActiveSectorEvent['level'], number> = {
      info: 0, success: 0, warning: 1, danger: 2,
    };
    for (const ev of events) {
      if (rank[ev.level] > rank[worst]) worst = ev.level;
    }
    switch (worst) {
      case 'danger': return COLORS.eventDangerNum;
      case 'warning': return COLORS.eventWarningNum;
      case 'success': return COLORS.eventSuccessNum;
      default: return COLORS.eventInfoNum;
    }
  }

  private eventRingColorCss(events: ActiveSectorEvent[]): string {
    return `#${this.eventRingColorNum(events).toString(16).padStart(6, '0')}`;
  }

  private formatEventCountdown(endsAt: number, now: number): string {
    const ms = endsAt - now;
    if (ms <= 0) return t('sectorEvent.expired');
    if (ms < 60_000) return t('sectorEvent.endsInLessThanMinute');
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const duration = `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
    return t('sectorEvent.endsIn', { duration });
  }

  /**
   * Populate the info-panel event card for the currently-selected
   * sector. Hidden when nothing is running there. Each event renders
   * its own block so overlapping events all stay visible.
   */
  private renderEventInfo(events: ActiveSectorEvent[]): void {
    while (this.eventInfoEl.firstChild) {
      this.eventInfoEl.removeChild(this.eventInfoEl.firstChild);
    }
    if (events.length === 0) {
      this.eventInfoEl.style.display = 'none';
      return;
    }
    this.eventInfoEl.style.display = 'flex';
    const tint = this.eventRingColorCss(events);
    this.eventInfoEl.style.borderColor = tint;
    this.eventInfoEl.style.boxShadow = `0 0 8px ${tint}40`;

    const heading = this.el('div', {
      fontSize: '9px',
      letterSpacing: '3px',
      color: tint,
      textTransform: 'uppercase',
      textShadow: `0 0 4px ${tint}`,
    }, t('sectorEvent.heading'));
    this.eventInfoEl.appendChild(heading);

    const now = Date.now();
    for (const ev of events) {
      const card = this.el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        marginTop: '2px',
      });
      const title = this.el('div', {
        fontSize: '12px',
        fontWeight: 'bold',
        letterSpacing: '1px',
        color: tint,
        textShadow: `0 0 4px ${tint}`,
      }, ev.title);
      card.appendChild(title);
      const body = this.el('div', {
        fontSize: '10px',
        color: 'rgba(220,235,255,0.85)',
        lineHeight: '1.4',
      }, ev.body);
      card.appendChild(body);
      const countdown = this.el('div', {
        fontSize: '10px',
        color: 'rgba(220,235,255,0.6)',
      }, this.formatEventCountdown(ev.endsAt, now));
      countdown.dataset['eventEndsAt'] = String(ev.endsAt);
      card.appendChild(countdown);
      this.eventInfoEl.appendChild(card);
    }
  }

  updatePlayerState(params: {
    discovered: SectorCoord[];
    credits: number;
    current?: SectorCoord;
    claims?: SectorClaim[];
    activeEvents?: ActiveSectorEvent[];
  }): void {
    this.discovered = params.discovered.map((d) => ({ ...d }));
    this.credits = params.credits;
    if (params.current) this.current = { ...params.current };
    this.syncClaims(params.claims);
    if (params.activeEvents !== undefined) {
      this.syncActiveEvents(params.activeEvents);
    }
    if (!this.visible) return;
    this.rebuildHologram();
    this.updateInfoPanel();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    cancelAnimationFrame(this.animFrame);
    this.root.style.display = 'none';
  }

  isVisible(): boolean {
    return this.visible;
  }

  setError(message: string): void {
    this.errorEl.textContent = message;
  }

  dispose(): void {
    this.hide();
    for (const mesh of this.sectorMeshes.values()) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    for (const line of this.linkLines) {
      line.geometry.dispose();
      (line.material as THREE.Material).dispose();
    }
    this.renderer?.dispose();
    this.renderer = null;
    this.root.remove();
  }
}
